import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import ModernAddressForm from '../components/ModernAddressForm';

function AddressesPage({ openModal, closeModal }) {
    const { token } = useAuth();
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchAddresses = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const data = await api('/api/addresses', { headers: { 'Authorization': `Bearer ${token}` } });
            setAddresses(data);
        } catch (err) {
            toast.error("Adresler yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchAddresses();
    }, [fetchAddresses]);

    const handleDelete = async (addressId) => {
        if (window.confirm("Bu adresi silmek istediğinizden emin misiniz?")) {
            try {
                await api(`/api/addresses/${addressId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                toast.success("Adres silindi.");
                fetchAddresses(); // Listeyi yenile
            } catch (err) {
                toast.error(err.message);
            }
        }
    };
    
    const openAddressModal = (address = null, addressType = 'delivery') => {
        if (openModal) {
            openModal(
                <div className="max-w-2xl mx-auto">
                    <ModernAddressForm 
                        addressToEdit={address} 
                        onFormSubmit={fetchAddresses} 
                        closeModal={closeModal}
                        defaultAddressType={addressType}
                    />
                </div>
            );
        }
    };

    const deliveryAddresses = addresses.filter(addr => addr.address_type === 'delivery');
    const billingAddresses = addresses.filter(addr => addr.address_type === 'billing');

    if (loading) {
        return (
            <div className="bg-transparent flex items-center justify-center py-20">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green mx-auto"></div>
                    <p className="text-center mt-4 text-gray-600">Adresler yükleniyor...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-transparent py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">Adres Bilgilerim</h1>
                    <p className="text-lg text-gray-600">Teslimat ve fatura adreslerinizi yönetin</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Teslimat Adresleri */}
                    <div className="bg-white rounded-3xl shadow-xl p-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="bg-brand-green/10 p-3 rounded-xl">
                                    <svg className="w-6 h-6 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">Teslimat Adreslerim</h3>
                                    <p className="text-sm text-gray-500">{deliveryAddresses.length} adres kayıtlı</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => openAddressModal && openAddressModal(null, 'delivery')} 
                                className="bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <span className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Ekle
                                </span>
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            {deliveryAddresses.length > 0 ? deliveryAddresses.map(addr => (
                                <ModernAddressCard 
                                    key={addr.id} 
                                    address={addr} 
                                    onEdit={() => openAddressModal && openAddressModal(addr)} 
                                    onDelete={() => handleDelete(addr.id)} 
                                />
                            )) : (
                                <div className="text-center py-12">
                                    <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        </svg>
                                    </div>
                                    <p className="text-gray-500 mb-4">Henüz teslimat adresiniz bulunmuyor</p>
                                    <button 
                                        onClick={() => openAddressModal && openAddressModal(null, 'delivery')}
                                        className="text-brand-green hover:text-brand-green-light font-medium"
                                    >
                                        İlk adresinizi ekleyin
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Fatura Adresleri */}
                    <div className="bg-white rounded-3xl shadow-xl p-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="bg-brand-green/10 p-3 rounded-xl">
                                    <svg className="w-6 h-6 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">Fatura Adreslerim</h3>
                                    <p className="text-sm text-gray-500">{billingAddresses.length} adres kayıtlı</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => openAddressModal && openAddressModal(null, 'billing')} 
                                className="bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <span className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Ekle
                                </span>
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            {billingAddresses.length > 0 ? billingAddresses.map(addr => (
                                <ModernAddressCard 
                                    key={addr.id} 
                                    address={addr} 
                                    onEdit={() => openAddressModal && openAddressModal(addr)} 
                                    onDelete={() => handleDelete(addr.id)} 
                                />
                            )) : (
                                <div className="text-center py-12">
                                    <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <p className="text-gray-500 mb-4">Henüz fatura adresiniz bulunmuyor</p>
                                    <button 
                                        onClick={() => openAddressModal && openAddressModal(null, 'billing')}
                                        className="text-brand-green hover:text-brand-green-light font-medium"
                                    >
                                        İlk adresinizi ekleyin
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const ModernAddressCard = ({ address, onEdit, onDelete }) => (
    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:border-brand-green/30 transition-all duration-300 hover:shadow-lg">
        <div className="flex justify-between items-start">
            <div className="flex-1">
                <div className="flex items-center space-x-2 mb-3">
                    <div className={`w-3 h-3 rounded-full ${address.address_type === 'delivery' ? 'bg-brand-green' : 'bg-blue-500'}`}></div>
                    <h4 className="font-bold text-gray-800">{address.address_title}</h4>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                    <p className="font-medium text-gray-700">{address.full_name}</p>
                    <p>{address.address_line}</p>
                    <p>{address.district}, {address.city}</p>
                    {address.neighborhood && <p>Mahalle: {address.neighborhood}</p>}
                    <div className="flex items-center space-x-1 mt-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span>{address.phone}</span>
                    </div>
                </div>
            </div>
            <div className="flex flex-col space-y-2 ml-4">
                <button 
                    onClick={onEdit} 
                    className="bg-brand-green/10 hover:bg-brand-green/20 text-brand-green px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Düzenle</span>
                </button>
                <button 
                    onClick={onDelete} 
                    className="bg-red-50 hover:bg-red-100 text-red-600 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Sil</span>
                </button>
            </div>
        </div>
    </div>
);


export default AddressesPage;